/**
 * Generates validation rules for text fields with customizable error messages.
 * @param {string} fieldLabel - Optional. The label of the field to customize the error messages.
 * @param {number} maxLength - Optional. Maximum allowed length of the input.
 * @param {boolean} isRequired - Optional. field is required true or false.
 * @returns {Array} Array of validation functions.
 */

// Fixed maximum lengths that can never be exceeded
const MAX_TEXT_LENGTH = 255; // The absolute maximum length for text fields
const MAX_ADDRESS_LENGTH = 200; // The absolute maximum length for address fields

const ValidationService = {
  store: null,
  init(opts) {
    if (opts.store) {
      this.store = opts.store;
    }
  },
  generateTextValidationRules(fieldLabel = "Input", inputMaxLength = MAX_TEXT_LENGTH, isRequired = true) {
    const effectiveMaxLength = inputMaxLength != null ? inputMaxLength : MAX_TEXT_LENGTH;

    let rules = [
      v => {
        if (v && typeof v === "string" && v.trim().length > effectiveMaxLength) {
          return `${fieldLabel} must be less than ${effectiveMaxLength} characters.`;
        } else {
          return true;
        }
      }
    ];
    if (isRequired) {
      rules.push(v => (!!v && typeof v === "string" && !!v.trim()) || `${fieldLabel} is required.`);
    }
    return rules;
  },
  generateAddressValidationRules(fieldLabel = "Address", inputMaxLength = MAX_ADDRESS_LENGTH, isRequired = true) {
    const effectiveMaxLength = inputMaxLength != null ? inputMaxLength : MAX_ADDRESS_LENGTH;
    let rules = [
      v => {
        if (v && v.trim()) {
          return (
            v?.trim().length <= effectiveMaxLength ||
            `${fieldLabel} must be less than ${effectiveMaxLength} characters.`
          );
        }
        return true;
      }
    ];
    if (isRequired) {
      rules.push(v => !!v?.trim() || `${fieldLabel} is required.`);
    }
    return rules;
  },
  generateEmailValidationRules(fieldLabel = "E-mail") {
    return [v => !!v?.trim() || `${fieldLabel} is required.`];
  },
  generateDropdownValidationRules(fieldLabel = "Selection") {
    return [v => (v && v !== "" && v.length !== 0) || `${fieldLabel} is required.`];
  },
  generateNotWhitespacesRule(fieldLabel = "Field") {
    return v => {
      if (v === "" || v === null || v === undefined) return true;
      return typeof v === "object" || v.trim().length > 0 || `${fieldLabel} cannot consist of only whitespaces.`;
    };
  },
  generateNonEmptyArrayRule(fieldLabel = "Field") {
    return [v => (Array.isArray(v) && v.length > 0) || `At least 1 ${fieldLabel} is required.`];
  },
  generateCheckboxValidationRules(fieldLabel = "Checkbox") {
    return [v => (Array.isArray(v) && v.length > 0) || `At lease one ${fieldLabel} must be checked.`];
  },
  generateSingleCheckboxValidationRules(fieldLabel = "Checkbox") {
    return [v => v === true || `${fieldLabel} is required.`];
  },

  generateDecimalValidationRules(fieldLabel = "Field", maxDecimals = 2, isRequired = true) {
    const regex = new RegExp(`^\\d*(\\.\\d{0,${maxDecimals}})?$`);
    let rules = [
      v => {
        if (isRequired && (v === "" || v === null || v === undefined)) {
          return `${fieldLabel} is required.`;
        }
        return (
          regex.test(v) || `${fieldLabel} must be a valid decimal number with up to ${maxDecimals} decimal places.`
        );
      }
    ];
    return rules;
  },
  generateDigitsValidationRules(fieldLabel = "Field", integerDigits = 10, fractionDigits = 2, isRequired = false) {
    const regex = new RegExp(`^(?:\\d{1,${integerDigits}})(?:\\.\\d{1,${fractionDigits}})?$`);
    return [
      v => {
        const isEmpty = v === "" || v === null || v === undefined;
        if (isEmpty) {
          return isRequired ? `${fieldLabel} is required.` : true;
        }
        const n = parseFloat(v);
        if (isNaN(n) || n < 0) {
          return `${fieldLabel} must be a non-negative number with up to ${integerDigits} integer digits and ${fractionDigits} decimal places.`;
        }
        return (
          regex.test(String(v).trim()) ||
          `${fieldLabel} must be a non-negative number with up to ${integerDigits} integer digits and ${fractionDigits} decimal places.`
        );
      }
    ];
  },
  generateIntegerValidationRules(fieldLabel = "Field", inputMaxLength = MAX_ADDRESS_LENGTH, isRequired = true) {
    const integerRegex = new RegExp("^\\d*$");
    const effectiveMaxLength = inputMaxLength != null ? inputMaxLength : MAX_TEXT_LENGTH;
    return [
      v => {
        if (v && v.trim().length > effectiveMaxLength) {
          return `${fieldLabel} must be less than ${effectiveMaxLength} characters.`;
        }
        if (isRequired && (v === "" || v === null || v === undefined)) {
          return `${fieldLabel} is required.`;
        }
        return v === null || integerRegex.test(v) || `${fieldLabel} must be a valid integer.`;
      }
    ];
  },
  generatePositiveIntegerValidationRules(fieldLabel = "Field") {
    return [v => !v || (Number.isInteger(Number(v)) && Number(v) > 0) || `${fieldLabel} must be a positive number`];
  },
  validatePositiveAmountRules(fieldLabel = "Field", isRequired = true, allowZero = false) {
    return v => {
      const isEmpty = v === null || v === undefined || (typeof v === "string" && v.trim() === "");

      if (isRequired && isEmpty) {
        return `${fieldLabel} is required.`;
      }

      if (!isRequired && isEmpty) {
        return true;
      }

      const cleaned = String(v).replace(/[$,]/g, "");
      const num = Number(cleaned);

      if (Number.isNaN(num)) {
        return `${fieldLabel} must be a valid number.`;
      }

      if (allowZero ? num < 0 : num <= 0) {
        return allowZero ? `${fieldLabel} cannot be negative.` : `${fieldLabel} must be greater than zero.`;
      }

      return true;
    };
  },
  generateDateValidationRules(fieldLabel = "Field", isRequired = true) {
    return [
      v => {
        if (isRequired && !v) {
          return `${fieldLabel} is required.`;
        }
        return true;
      }
    ];
  },
  generateNameCharacterValidationRule(fieldLabel = "Name") {
    return v =>
      !v ||
      /^[\p{L} .'-]+$/u.test(v) ||
      `${fieldLabel} can only contain letters, spaces, hyphens (-), apostrophes ('), and periods (.)`;
  },
  generateTinValidationRules(fieldLabel = "TIN", rawvalue = null, isRequired = true) {
    return [
      v => {
        const cleanedTin = ((v && rawvalue) || "").replace(/\D/g, "");
        if (cleanedTin && isRequired) {
          if (this.isSequential(cleanedTin))
            return `${fieldLabel} Please enter a valid Social Security Number. Numbers with sequential digits are not permitted (e.g., 123456789 or 987654321).`;
          else if (/^(\d)\1{8}$/.test(cleanedTin)) {
            return `${fieldLabel} Please enter a valid Social Security Number. Numbers like all zeroes or repeated digits are not allowed.`;
          }
        }
        return true;
      }
    ];
  },

  isSequential(tin) {
    if (!tin || tin.length < 2) return false;

    let increasing = true;
    let decreasing = true;

    for (let i = 1; i < tin.length; i++) {
      const prev = parseInt(tin[i - 1], 10);
      const curr = parseInt(tin[i], 10);

      if (curr !== prev + 1) increasing = false;
      if (curr !== prev - 1) decreasing = false;

      if (!increasing && !decreasing) break;
    }

    return increasing || decreasing;
  },
  generateMinMaxValidationRules(fieldLabel, getOtherValue, options = {}) {
    const { minValue = 0, allowEmpty = true, isMinField = true, required = false } = options;

    return [
      v => {
        const isEmpty = v === null || v === undefined || (typeof v === "string" && v.trim() === "");

        if (isEmpty) {
          if (required) {
            return `${fieldLabel} is required`;
          }
          if (allowEmpty) {
            return true;
          }
        }

        const num = Number(v);
        if (isNaN(num)) {
          return `${fieldLabel} must be a number`;
        }

        if (num < minValue) {
          return `${fieldLabel} must be ${minValue} or greater`;
        }

        // Compare with the other field
        const otherRaw = typeof getOtherValue === "function" ? getOtherValue() : undefined;
        const other = otherRaw !== null && otherRaw !== undefined && otherRaw !== "" ? Number(otherRaw) : NaN;

        if (!isNaN(other)) {
          if (isMinField && num > other) {
            return `${fieldLabel} must be less than or equal to Maximum (${other})`;
          }
          if (!isMinField && num < other) {
            return `${fieldLabel} must be greater than or equal to Minimum (${other})`;
          }
        }

        return true;
      }
    ];
  },
  generateDateRangeValidationRules(
    startLabel = "Effective Date",
    endLabel = "Expiration Date",
    startDateGetter,
    endDateGetter,
    isRequired = false
  ) {
    return [
      v => {
        if (isRequired && !v) return `${startLabel} is required.`;

        const startDate = startDateGetter ? startDateGetter() : null;
        const endDate = endDateGetter ? endDateGetter() : null;

        if (v && startDate && endDate && new Date(startDate) > new Date(endDate)) {
          return `${startLabel} cannot be after ${endLabel}.`;
        }

        return true;
      },
      v => {
        if (isRequired && !v) return `${endLabel} is required.`;

        const startDate = startDateGetter ? startDateGetter() : null;
        const endDate = endDateGetter ? endDateGetter() : null;

        if (v && startDate && endDate && new Date(endDate) < new Date(startDate)) {
          return `${endLabel} cannot be before ${startLabel}.`;
        }

        return true;
      }
    ];
  }
};

export default ValidationService;
